import { z } from "zod";
import { LIMITS } from "./limits.ts";
import { isIsoDate } from "./dates.ts";
import { emailSchema, passwordSchema, personNameSchema } from "./auth.ts";

export const uuid = z.uuid({ error: "Identificador inválido." });
export const version = z.number({ error: "Versão ausente." }).int().positive();

export const isoDate = (label = "Informe uma data válida.") =>
  z.string({ error: label }).refine(isIsoDate, label);

export const requiredText = (max: number, label: string) =>
  z.string({ error: label }).trim().min(1, label).max(max, `Use no máximo ${max} caracteres.`);

/** Trim; empty becomes null (docs/03: trim-empty means missing). */
export const optionalText = (max: number) =>
  z
    .string()
    .max(max * 2)
    .nullish()
    .transform((v) => (v == null ? null : v.trim() === "" ? null : v.trim()))
    .refine((v) => v === null || v.length <= max, `Use no máximo ${max} caracteres.`);

export const reasonSchema = requiredText(LIMITS.reason, "Informe a justificativa.");

const planFields = {
  name: requiredText(LIMITS.name, "Informe o nome da oficina."),
  facilitator_name: requiredText(LIMITS.name, "Informe o facilitador."),
  starts_on: isoDate("Informe a data de início."),
  ends_on: isoDate("Informe a data de término."),
};

const endsAfterStart = <T extends { starts_on: string; ends_on: string }>(v: T) => v.ends_on >= v.starts_on;
const endsMessage = { path: ["ends_on"], message: "O término não pode ser anterior ao início." };

export const planCreateSchema = z
  .object({
    institution_id: uuid,
    ...planFields,
    /** Local date (America/Fortaleza); access ends at 23:59:59.999 of that day. */
    access_until: isoDate("Informe até quando os participantes terão acesso."),
    confirm_early_cutoff: z.boolean().optional().default(false),
  })
  .refine(endsAfterStart, endsMessage);

export const planUpdateSchema = z
  .object({
    expected_version: version,
    ...planFields,
    /** Renames the plan's institution (shared by every workshop of that institution). */
    institution_name: requiredText(LIMITS.name, "Informe o nome da instituição.").optional(),
  })
  .refine(endsAfterStart, endsMessage);

export const accessDeadlineSchema = z.object({
  expected_version: version,
  access_until: isoDate("Informe a nova data de acesso."),
  reason: reasonSchema,
  confirm_immediate_cutoff: z.boolean().optional().default(false),
});

const color = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Use uma cor no formato #RRGGBB.");

export const axisCreateSchema = z.object({
  name: requiredText(LIMITS.name, "Informe o nome do eixo."),
  color: color.default("#1F6FB2"),
  sort_order: z.number().int().optional(),
  editor_group_id: uuid.nullish(),
});

export const axisUpdateSchema = z.object({
  expected_version: version,
  name: requiredText(LIMITS.name, "Informe o nome do eixo.").optional(),
  color: color.optional(),
  sort_order: z.number().int().optional(),
  editor_group_id: uuid.nullish(),
});

export const namedCreateSchema = (label: string) => z.object({ name: requiredText(LIMITS.name, label) });
export const namedUpdateSchema = (label: string) => z.object({ expected_version: version, name: requiredText(LIMITS.name, label) });

export const archiveSchema = z.object({ expected_version: version, reason: optionalText(LIMITS.reason) });

/** BLD-28: the group's manager ("gestor"), or null to clear. */
export const groupManagerSchema = z.object({ expected_version: version, membership_id: uuid.nullable() });
export const groupMembersSchema = z.object({
  expected_version: version,
  membership_ids: z.array(uuid).max(LIMITS.collection),
});

export const membershipUpdateSchema = z
  .object({
    expected_version: version,
    role: z.enum(["instructor", "participant"]).optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => v.role !== undefined || v.active !== undefined, { message: "Nada para alterar.", path: ["role"] });

export const invitationCreateSchema = z.object({
  email: emailSchema,
  role: z.enum(["instructor", "participant"]).default("participant"),
  group_ids: z.array(uuid).max(50).default([]),
});

export const invitationTokenSchema = z.object({ token: z.string().min(16).max(200) });

export const invitationAcceptSchema = z.object({
  token: z.string().min(16).max(200),
  name: personNameSchema.optional(),
  password: passwordSchema.optional(),
});

export type InvitationStatus = "pending" | "accepted" | "revoked" | "expired";
